import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  ShoppingBag,
  LocalShipping,
  Payment,
  CheckCircle,
  Add,
  Edit,
  Delete,
  Close,
  Home,
} from '@mui/icons-material';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { addressAPI, orderAPI } from '../services/api';
import { currencyService } from '../services/currencyService';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const {  userCountry, exchangeRate, isIndia } = useCurrency();


  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [loading, setLoading] = useState(true);
  const [orderPlaced, setOrderPlaced] = useState(false);

  const [addressForm, setAddressForm] = useState({
    name: '',
    phone: '',
    addressLine: '',
    city: '',
    state: '',
    pincode: '',
    isDefault: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Don't redirect while loading auth state
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchAddresses();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await addressAPI.getAll();
      const addressList = response.data || response || [];
      setAddresses(addressList);

      // Auto-select default address or first address
      const defaultAddr = addressList.find(addr => addr.isDefault);
      if (defaultAddr) {
        setSelectedAddress(defaultAddr._id);
      } else if (addressList.length > 0) {
        setSelectedAddress(addressList[0]._id);
      }
    } catch (err) {
      console.error('Error fetching addresses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddressFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setAddressForm({
      ...addressForm,
      [name]: type === 'checkbox' ? checked : value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateAddressForm = () => {
    const newErrors = {};
    if (!addressForm.name.trim()) newErrors.name = 'Name is required';
    if (!addressForm.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(addressForm.phone)) newErrors.phone = 'Phone must be 10 digits';
    if (!addressForm.addressLine.trim()) newErrors.addressLine = 'Address is required';
    if (!addressForm.city.trim()) newErrors.city = 'City is required';
    if (!addressForm.state.trim()) newErrors.state = 'State is required';
    if (!addressForm.pincode.trim()) newErrors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(addressForm.pincode)) newErrors.pincode = 'Pincode must be 6 digits';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddAddress = () => {
    setEditingAddress(null);
    setAddressForm({
      name: user?.name || '',
      phone: user?.phone || '',
      addressLine: '',
      city: '',
      state: '',
      pincode: '',
      isDefault: addresses.length === 0,
    });
    setShowAddressForm(true);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setAddressForm({
      name: address.name || '',
      phone: address.phone || '',
      addressLine: address.addressLine || '',
      city: address.city || '',
      state: address.state || '',
      pincode: address.pincode || '',
      isDefault: address.isDefault || false,
    });
    setShowAddressForm(true);
  };

  const handleSaveAddress = async () => {
    if (!validateAddressForm()) return;

    try {
      if (editingAddress) {
        await addressAPI.update(editingAddress._id, addressForm);
      } else {
        await addressAPI.add(addressForm);
      }
      setShowAddressForm(false);
      fetchAddresses();
    } catch (err) {
      console.error('Error saving address:', err);
      alert(err.message || 'Failed to save address');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;

    try {
      await addressAPI.delete(addressId);
      fetchAddresses();
      if (selectedAddress === addressId) {
        setSelectedAddress(null);
      }
    } catch (err) {
      console.error('Error deleting address:', err);
      alert('Failed to delete address');
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      alert('Please select a delivery address');
      return;
    }

    const selectedAddr = addresses.find(addr => addr._id === selectedAddress);
    if (!selectedAddr) {
      alert('Selected address not found');
      return;
    }

    setLoading(true);
    try {
      // Prepare order data with currency information
      const orderData = {
        items: cartItems.map(item => ({
          variantId: item.selectedVariant?._id || item.selectedVariant?.id,
          quantity: item.quantity,
          price: item.price, // Original INR price
        })),
        shippingAddress: `${selectedAddr.name}, ${selectedAddr.phone}\n${selectedAddr.addressLine}\n${selectedAddr.city}, ${selectedAddr.state} - ${selectedAddr.pincode}`,
        paymentMethod: paymentMethod.toUpperCase(),
        currency: isIndia ? 'INR' : 'USD',
        exchangeRate: isIndia ? 1 : exchangeRate,
      };

      // Use currency service to prepare complete order data
      const preparedOrderData = currencyService.prepareOrderData(
        orderData,
        userCountry,
        exchangeRate
      );

      console.log('Placing order:', preparedOrderData);
      console.log('Original subtotal:', subtotal);
      console.log('User country:', userCountry);
      console.log('Exchange rate:', exchangeRate);
      console.log('Display total amount:', preparedOrderData.displayTotalAmount);

      const response = await orderAPI.create(preparedOrderData);
      console.log('Order response:', response);

      setOrderPlaced(true);
      clearCart();

      setTimeout(() => {
        navigate('/orders');
      }, 2000);
    } catch (error) {
      console.error('Order placement error:', error);
      alert(error.message || 'Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0 && !orderPlaced) {
    return (
      <Box sx={{ pt: 12, pb: 8, minHeight: '60vh' }}>
        <Container>
          <Box sx={{ textAlign: 'center' }}>
            <ShoppingBag sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Your cart is empty
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/shop')}
              sx={{
                mt: 3,
                borderRadius: 50,
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
              }}
            >
              Continue Shopping
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  if (orderPlaced) {
    return (
      <Box sx={{ pt: 12, pb: 8, minHeight: '60vh' }}>
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 100, color: 'success.main', mb: 3 }} />
            <Typography
              className='font2'
              variant="h3"
              sx={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 700,
                mb: 2,
              }}
            >
              Order Placed Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Thank you for your order. We'll send you a confirmation email shortly.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting to orders page...
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  const subtotal = getCartTotal();
  const shipping = 0;
  const total = subtotal + shipping;

  console.log(subtotal,"'subtotalsubtotal'")

  // Get currency symbol from first cart item
  const currencySymbol = cartItems[0]?.selectedVariant?.currencySymbol || '$';

  // Convert to display currency
  const displaySubtotal = `${currencySymbol}${subtotal.toFixed(2)}`;
  const displayShipping = shipping === 0 ? 'FREE' : `${currencySymbol}${shipping.toFixed(2)}`;
  const displayTotal = `${currencySymbol}${total.toFixed(2)}`;

  return (
    <Box sx={{ pt: { xs: 9, md: 10 }, pb: 8, bgcolor: '#F8F6F1' }}>
      <Container maxWidth="lg">
        <Typography
          className='font2'
          variant="h3"
          sx={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 700,
            mb: 4,
            textAlign: 'center',
          }}
        >
          Checkout
        </Typography>

        <Grid container spacing={4}>
          {/* Delivery Address */}
          <Grid item xs={12} md={7}>
            <Card sx={{ mb: 3 }}>
              <CardContent className='p-15' sx={{ p: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <LocalShipping className='font14' sx={{ color: 'primary.main', fontSize: 32 }} />
                    <Typography className='font12 ml-5' variant="h5" sx={{ fontWeight: 600 }}>
                      Delivery Address
                    </Typography>
                  </Stack>
                  <Button
                    startIcon={<Add />}
                    onClick={handleAddAddress}
                    sx={{
                      borderRadius: 50,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Add New
                  </Button>
                </Stack>

                {loading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : addresses.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Home sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary" gutterBottom>
                      No saved addresses
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleAddAddress}
                      sx={{ mt: 2, borderRadius: 50 }}
                    >
                      Add Address
                    </Button>
                  </Box>
                ) : (
                  <RadioGroup value={selectedAddress} onChange={(e) => setSelectedAddress(e.target.value)}>
                    <Stack spacing={2}>
                      {addresses.map((address) => (
                        <Card
                          key={address._id}
                          sx={{
                            border: '2px solid',
                            borderColor: selectedAddress === address._id ? 'primary.main' : 'divider',
                            cursor: 'pointer',
                          }}
                          onClick={() => setSelectedAddress(address._id)}
                        >
                          <CardContent className='p-5' sx={{ p: 2 }}>
                            <Stack direction="row" spacing={2}>
                              <Radio className='p-5' value={address._id} />
                              <Box className='ml-5' sx={{ flex: 1 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                  <Typography className='line-height-cst font09' variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {address.name}
                                  </Typography>
                                  {address.isDefault && (
                                    <Chip label="Default" size="small" color="primary" />
                                  )}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                  {address.phone}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {address.addressLine}
                                </Typography>

                                <Typography variant="body2" color="text.secondary">
                                  {address.city}, {address.state} - {address.pincode}
                                </Typography>
                              </Box>
                              <Stack className='ml-5' direction="row" spacing={1}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditAddress(address);
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteAddress(address._id);
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </RadioGroup>
                )}
              </CardContent>
            </Card>

            {/* Payment Method */}
            <Card>
              <CardContent className='p-15' sx={{ p: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                  <Payment className='font15' sx={{ color: 'primary.main', fontSize: 32 }} />
                  <Typography className='font14' variant="h5" sx={{ fontWeight: 600 }}>
                    Payment Method
                  </Typography>
                </Stack>

                <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                  <FormControlLabel
                    value="cod"
                    control={<Radio />}
                    label="Cash on Delivery"
                  />
                  <FormControlLabel
                    value="online"
                    control={<Radio />}
                    label="Online Payment (Coming Soon)"
                    disabled
                  />
                </RadioGroup>
              </CardContent>
            </Card>
          </Grid>

          {/* Order Summary */}
          <Grid item xs={12} md={5}>
            <Card sx={{ position: 'sticky', top: 100 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                  Order Summary
                </Typography>

                <Stack spacing={2} sx={{ mb: 3 }}>
                  {cartItems.map((item) => (
                    <Box key={item.itemId}>
                      <Stack direction="row" spacing={2}>
                        <Box
                          component="img"
                          src={item.image}
                          alt={item.name}
                          sx={{
                            width: 60,
                            height: 60,
                            objectFit: 'cover',
                            borderRadius: 1,
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.name}
                          </Typography>
                          {item.selectedVariant && (
                            <Typography variant="caption" color="text.secondary">
                              {item.selectedVariant.weight} - {item.selectedVariant.packaging}
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary">
                            Qty: {item.quantity}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.selectedVariant.currencySymbol}{item.price * item.quantity}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {displaySubtotal}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Shipping:</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: shipping === 0 ? 'success.main' : 'text.primary' }}>
                      {displayShipping}
                    </Typography>
                  </Stack>
                  {/* {subtotal < 999 && (
                    <Alert severity="info" sx={{ fontSize: '0.75rem' }}>
                      Add {currencySymbol}{(999 - subtotal).toFixed(2)} more for free shipping!
                    </Alert>
                  )} */}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" justifyContent="space-between" sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Total:
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontWeight: 700,
                      color: 'primary.main',
                    }}
                  >
                    {displayTotal}
                  </Typography>
                </Stack>

                <Button
                  className='form-btn'
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handlePlaceOrder}
                  disabled={!selectedAddress}
                  sx={{
                    py: 1.5,
                    borderRadius: 50,
                    background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
                    fontWeight: 600,
                    fontSize: '1rem',
                    textTransform: 'none',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                    },
                  }}
                >
                  Place Order
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Address Form Dialog - Improved Design */}
      <Dialog
        open={showAddressForm}
        onClose={() => setShowAddressForm(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: "custom-dialog-paper",
          sx: {
            borderRadius: 3,
            boxShadow: '0 24px 48px rgba(201, 169, 97, 0.2)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography
                className='font14'
                variant="h4"
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 700,
                  color: 'primary.dark',
                  mb: 0.5,
                }}
              >
                {editingAddress ? 'Edit Address' : 'Add New Address'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please fill in your delivery details
              </Typography>
            </Box>
            <IconButton
              className='p-5 font12'
              onClick={() => setShowAddressForm(false)}
              sx={{
                bgcolor: 'background.default',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3, pb: 2 }} className='p-15'>
          <Stack spacing={3}>
            {/* Personal Information */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: 'primary.main',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Personal Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={addressForm.name}
                    onChange={handleAddressFormChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={addressForm.phone}
                    onChange={handleAddressFormChange}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    required
                    variant="outlined"
                    placeholder="10 digit mobile number"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Address Details */}
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: 'primary.main',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Address Details
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Complete Address"
                  name="addressLine"
                  value={addressForm.addressLine}
                  onChange={handleAddressFormChange}
                  error={!!errors.addressLine}
                  helperText={errors.addressLine || "House No, Building Name, Street, Area"}
                  required
                  multiline
                  rows={2}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                />

                <Grid container spacing={2} className='grid-w'>
                  <Grid item xs={12} sm={6} className='grid-cst'>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={addressForm.city}
                      onChange={handleAddressFormChange}
                      error={!!errors.city}
                      helperText={errors.city}
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} className='grid-cst'>
                    <TextField
                      fullWidth
                      label="State"
                      name="state"
                      value={addressForm.state}
                      onChange={handleAddressFormChange}
                      error={!!errors.state}
                      helperText={errors.state}
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} className='grid-cst'>
                    <TextField
                      fullWidth
                      label="Pincode"
                      name="pincode"
                      value={addressForm.pincode}
                      onChange={handleAddressFormChange}
                      error={!!errors.pincode}
                      helperText={errors.pincode || "6 digit pincode"}
                      required
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Box>

            {/* Default Address */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <FormControlLabel
                control={
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={addressForm.isDefault}
                    onChange={handleAddressFormChange}
                    style={{
                      width: 18,
                      height: 18,
                      cursor: 'pointer',
                      accentColor: '#C9A961',
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>
                    Set as default delivery address
                  </Typography>
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 3, gap: 2 }} className='p-10'>
          <Button
            className='px-15 font08'
            onClick={() => setShowAddressForm(false)}
            variant="outlined"
            sx={{
              borderRadius: 50,
              px: 4,
              py: 1.2,
              borderColor: 'divider',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'transparent',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            className='ml-5 px-15 font08'
            variant="contained"
            onClick={handleSaveAddress}
            sx={{
              borderRadius: 50,
              px: 5,
              py: 1.2,
              background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
              boxShadow: '0 4px 12px rgba(201, 169, 97, 0.3)',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                boxShadow: '0 6px 16px rgba(201, 169, 97, 0.4)',
              },
            }}
          >
            Save Address
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CheckoutPage;
